/*globals login, showModalInfo, showModalConfirm, globalNavigate, rulesRate, rulesFlow, rulesMonitor*/

import React, { useContext } from "react";
import styled from "styled-components";
import Request from "common/Request";
import DataTable, { Column } from "common/DataTable";
import { openPolicy } from "views/Status/Subscribers/Report/ListSubscribers";
import ActionsContext from "common/ActionsContext";
import useProfile from "common/hooks/useProfile";

const returnView = "viewSubscriberAttributes";

const TableContainer = styled.div`
  width: 100%;
  overflow: auto;
  padding-top: 26px;
`;

const handleWarnings = (response) => {
  const confirmedRE = /^Removed \d+ API subscriber/
  if (response.match(confirmedRE) !== null) {
    return
  }
  const notFoundRE = /^%WARN-ENOENT: Cannot find any "(.*)" API subscribers/;
  if (response.match(notFoundRE) !== null) {
    return;
  }
  showModalInfo("Warning:", response);
};

const confirmCreatePolicy = (policy) =>
  new Promise((resolve, reject) =>
    showModalConfirm(
      `Confirm before continue`,
      `Warning: This policy is undefined, and has been assigned to some subscribers or subscriber-groups. Those subscribers are now getting a default policy from the configured rules, while thos subscriber-groups are getting no rate limitation. If you click "Define", you can now define this policy and then the assigned subscribers and groups will get it.`,
      "warning",
      () => resolve(policy),
      () => reject(),
      "DEFINE",
      "RETURN"
    )
  );

const offerDefinePolicyIfNotFound = (name, settings={}) =>
  confirmCreatePolicy(name)
    .then( policy => {
      globalNavigate("viewPolicyRateEdit", {...settings, returnView, create: policy});
    });

const policyNotFoundRE = /%ERR-ENOENT: Cannot find "(.*)" policy/

const policyMayNotExist = outcome => {
  const notFoundMatch = outcome.match(policyNotFoundRE);
  if ( notFoundMatch === null ) {
    throw outcome;
  }
  const [_all, policyName] = notFoundMatch;
  return offerDefinePolicyIfNotFound(policyName)
}

const openOrCreatePolicy = (policyName, ...args) =>
  ifCl.run(`show policy ${policyName}`)
    .then(() => openPolicy(policyName, ...args))
    .catch(policyMayNotExist);

const confirmBeforeDeleteSubscriber = (ip) =>
  new Promise((resolve, reject) =>
    showModalConfirm(
      "Do you want to proceed ?",
      `You are removing any policy and subscriber-id assignments for <span class="col-blue">${ip} </span>, performed by RADIUS, the REST API or a billing integration, as well as all quotas. This will also remove <span class="col-blue">${ip}</span> from all subscriber groups. This will not remove <span class="col-blue">${ip}</span> from the BQN, just those assignments (if any). The <span class="col-blue">${ip}</span> subscriber will now be assigned to a new policy, according to the configured Subscriber Rate rules.`,
      "warning",
      () => resolve(ip),
      () => reject()
    )
  );

const deleteSubscriber = (ip) =>
  ifCl.run(`clear api subscriber ${ip}`).then(handleWarnings);

const openSubscriberDetails = ({ addr, subsId }) => {
  globalNavigate("viewSubsDetail", { returnView, addr, subsId });
};

const openSubscriberGroupsOn = (ipAddress) => {
  globalNavigate("viewStatusSubscriberGroups", { returnView, ipAddress });
};

const openSubscriberQuotas = (subnet) => {
  if (subnet !== "n/a") {
    globalNavigate("viewSubscriberQuotas", { returnView, subnet });
  }
};

const doesNothing = () => {};

const getColumns = ({ profile = {}, doReload = doesNothing }) => [
  Column.IPAddress({
    label: "IP-ADDRESS",
    idx: 0,
    colClassName: "align-left",
    onClick: (addr, _row, _trigger) => openSubscriberDetails({ addr }),
  }),
  Column.Text({
    label: "SUBSCRIBER-ID",
    idx: 1,
    precision: 0,
    colClassName: "align-left",
    cellClassName: (value) => (value !== "n/a" ? "hyperlink-text" : ""),
    onClick: (subsId, [addr, ..._row], _trigger) => {
      if (subsId === "n/a") {
        return;
      }
      openSubscriberDetails({ subsId });
    },
  }),
  Column.Text({
    label: "RATE-POLICY",
    idx: 9,
    cellClassName: (value, row) =>
      `align-left${
        profile.hideRates !== true && value !== "n/a" ? " hyperlink-text" : ""
      }${row[9] === "yes" ? " col-red  cell-with-icon-left" : ""}`,
    onClick:
      profile.hideRates === true
        ? null
        : (policyName, _row, _trigger) =>
            policyName !== "n/a"
              ? openOrCreatePolicy(policyName, _row[9] === "yes")
              : null,
    icon: (row) => {
      return row[9] === "yes" ? "warning" : null;
    },
  }),
  Column.Text({
    label: "ASSIGNED-BY",
    idx: 10,
    colClassName: "align-left",
  }),
  ...(login.isPlatform("gs-kr")
    ? []
    : [
        Column.Text({
          label: "BILLING-BLOCK",
          idx: 8,
          colClassName: "align-left",
        }),
      ]),
  Column.Text({
    label: "QUOTAS",
    idx: 11,
    colClassName: "align-left",
    cellClassName: (value) => (value !== "n/a" ? "hyperlink-text" : ""),
    onClick: (_value, [subnet, ...row]) => {
      openSubscriberQuotas(subnet);
    },
  }),
  Column.Text({
    label: "SUBSCRIBER-GROUPS",
    idx: 16,
    cellClassName: "align-right hyperlink-text",
    onClick: (value, [ipAdress]) => {
      openSubscriberGroupsOn(ipAdress);
    },
  }),
  Column.Time({
    label: "UPDATE",
    idx: 15,
    colClassName: "align-right",
  }),
  Column.Text({
    label: "ACTIVE",
    idx: 17,
    colClassName: "align-left",
  }),
  Column.Hidden({
    label: "UNDEFINED",
    idx: 18,
  }),
  Column.Actions({
    label: "ACTIONS",
    idx: 0,
    colClassName: "text-center action",
    cellClassName: "hides-content-to-operators",
    are: [
      {
        id: "delete",
        label: "Delete",
        icon: "delete",
        onClick: (addr) =>
          confirmBeforeDeleteSubscriber(addr)
            .then(deleteSubscriber)
            .then(doReload),
      },
    ],
  }),
];

const onlyMatchingSource =
  (source) =>
  ([_address, _subId, , target]) =>
    target === source;

const ListSubscribers = ({ request, source }) => {
  const actions = useContext(ActionsContext);
  const profile = useProfile();
  const doReload = () => actions.send("do-load");
  return profile === null ? null : (
    <TableContainer>
      <Request during={request}>
        {({ response }) => (
          <DataTable
            pageLength={100}
            exportAsCSV={true}
            columns={getColumns({ profile, doReload })}
            filters={
              source === "all" ? undefined : [onlyMatchingSource(source)]
            }
            content={response}
          />
        )}
      </Request>
    </TableContainer>
  );
};

export default ListSubscribers;
